import {
  type AndOrCondition,
  type PropertyCondition,
  type NestableCondition,
  StringOperator,
  ColumnType,
  ConditionType,
  DecisionEngineInteractionColumnNames,
} from "../types";

/**
 * Get the subconditions to filter on for the selected metric (outcome model)
 * Along with the ad hoc filters users can add to a selected metric, we also
 * want to include the outcome's filter conditions. The outcome's filter is configured
 * in the AID and is used to filter event models to define certain outcomes (e.g.
 * purchase/subscribe/etc event types). We don't want to expose this filter to the
 * the user, but we do want to include it in the metric calculation so the BE can filter
 * the underlying event model accordingly.
 */
export const getFilterSubconditionsForMetric = ({
  metricConditions,
  campaignIdColumn,
  flowMessageCampaignIds,
  customerManagedCampaignIds = [],
}: {
  metricConditions: AndOrCondition<NestableCondition>[];
  campaignIdColumn?: string | null;
  flowMessageCampaignIds: string[];
  customerManagedCampaignIds?: string[];
}): AndOrCondition<NestableCondition>[] => {
  const filterConditions: AndOrCondition<NestableCondition>[] = [
    ...(campaignIdColumn
      ? [
          {
            type: ConditionType.And,
            conditions: [
              {
                type: ConditionType.Property,
                propertyType: ColumnType.String,
                property: campaignIdColumn,
                // We use contains here because we want to be able to match any substrings of the campaign ID
                // e.g. something like `hightouch_aid_campaign_...` should match to `hightouch_aid_campaign_04/24/2025`
                operator: StringOperator.Contains,
                value: [
                  ...flowMessageCampaignIds,
                  ...customerManagedCampaignIds,
                ],
              },
              {
                type: ConditionType.Property,
                property: {
                  type: "decision_engine_interaction",
                  name: DecisionEngineInteractionColumnNames.Experiment,
                },
                propertyType: ColumnType.String,
                operator: StringOperator.DoesNotEqual,
                // If this outcome is associated with a campaign id, we always want to exclude the holdout group
                // If we don't have additional customer managed campaign ids, we also want to exclude the customer_managed group
                value:
                  customerManagedCampaignIds.length > 0
                    ? ["holdout"]
                    : ["holdout", "customer_managed"],
              },
            ],
          } satisfies AndOrCondition<PropertyCondition>,
        ]
      : []),
    ...metricConditions,
  ];

  return toSingleCondition(filterConditions);
};

function toSingleCondition(
  conditions: AndOrCondition<NestableCondition>[],
): AndOrCondition<NestableCondition>[] {
  if (conditions.length === 0) {
    return [];
  }

  if (conditions.length === 1) {
    return conditions;
  }

  return [{ type: ConditionType.And, conditions }];
}

// Reference `pullOutcomesTable`
export const getCampaignIdsFromFlowMessages = (
  flowMessages: {
    config: {
      campaignId?: string | number;
      resourceId?: string | number;
      attributionCampaignIds?: (string | number)[];
    };
  }[],
): string[] => {
  return flowMessages
    .flatMap(({ config }) => {
      const campaignIds: string[] = [];

      if (config?.campaignId) {
        campaignIds.push(config.campaignId.toString());
      }
      if (config?.resourceId) {
        campaignIds.push(config.resourceId.toString());
      }
      if (config?.attributionCampaignIds) {
        campaignIds.push(...config.attributionCampaignIds.map(String));
      }

      return campaignIds;
    })
    .sort();
};

export const getCampaignIdsFromCustomerManaged = (flowConfig?: {
  customer_managed_campaign_ids?: string[];
}): string[] => {
  return flowConfig?.customer_managed_campaign_ids ?? [];
};
